import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Container,
  Text,
  Card,
  CardBody,
  Stack,
  HStack,
  IconButton,
  Textarea,
  FormHelperText,
} from "@chakra-ui/react";
import { Formik, Form, Field } from "formik";
import { ChevronLeftIcon, AddIcon, DeleteIcon } from "@chakra-ui/icons";
import { createConnection } from "../../services/IntegrationService";
import { CreateUpdateConnectionRequest, DataWarehouseType } from "build-ts/thoughtspot";
import MetadataExplorer from "./MetadataExplorer";
import showToast from "hooks/useCustomToast";

// Define warehouse-specific configuration types
interface BigQueryConfig {
  projectId: string;
  serviceAccountKey: string;
  additionalParams: { key: string; value: string }[];
}

interface SnowflakeConfig {
  accountName: string;
  warehouse: string;
  database: string;
  schema: string;
  role: string;
  additionalParams: { key: string; value: string }[];
}

interface RedshiftConfig {
  database: string;
  host: string;
  port: string;
  username: string;
  password: string;
  additionalParams: { key: string; value: string }[];
}

interface StepOneValues {
  connectionName: string;
  description: string;
  dataWarehouseType: DataWarehouseType;
}

interface ConnectionFormValues extends StepOneValues {
  bigQueryConfig?: BigQueryConfig;
  snowflakeConfig?: SnowflakeConfig;
  redshiftConfig?: RedshiftConfig;
}

const WAREHOUSE_TYPES = [
  { id: DataWarehouseType.GOOGLE_BIGQUERY, name: "Google BigQuery", icon: "🔍" },
  { id: DataWarehouseType.SNOWFLAKE, name: "Snowflake", icon: "❄️" },
  { id: DataWarehouseType.POSTGRES, name: "PostgreSQL", icon: "📊" },
  { id: DataWarehouseType.SQLSERVER, name: "SQL Server", icon: "📊" },
];

// Define warehouse type mapping to match backend enum
const WAREHOUSE_TYPE_MAPPING = {
  [DataWarehouseType.GOOGLE_BIGQUERY]: DataWarehouseType.GOOGLE_BIGQUERY,
  [DataWarehouseType.SNOWFLAKE]: DataWarehouseType.SNOWFLAKE,
  [DataWarehouseType.POSTGRES]: DataWarehouseType.POSTGRES,
  [DataWarehouseType.SQLSERVER]: DataWarehouseType.SQLSERVER,
} as const;

const IntegrationPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedType, setSelectedType] = useState<DataWarehouseType>(DataWarehouseType.INVALID_DATA_WAREHOUSE_TYPE);
  const [connectionId, setConnectionId] = useState<string>("");

  const initialValues: ConnectionFormValues = {
    connectionName: "",
    description: "",
    dataWarehouseType: DataWarehouseType.INVALID_DATA_WAREHOUSE_TYPE,
    bigQueryConfig: {
      projectId: "",
      serviceAccountKey: "",
      additionalParams: [],
    },
    snowflakeConfig: {
      accountName: "",
      warehouse: "",
      database: "",
      schema: "",
      role: "",
      additionalParams: [],
    },
    redshiftConfig: {
      database: "",
      host: "",
      port: "5439",
      username: "",
      password: "",
      additionalParams: [],
    },
  };

  const formatConnectionConfig = (values: ConnectionFormValues) => {
    const config: any = {
      authType: "SERVICE_ACCOUNT",
      configuration: {},
    };

    switch (values.dataWarehouseType) {
      case DataWarehouseType.GOOGLE_BIGQUERY:
        config.configuration = {
          project_id: values.bigQueryConfig?.projectId,
          oauth_pvt_key: values.bigQueryConfig?.serviceAccountKey,
        };
        // Add additional params if any
        values.bigQueryConfig?.additionalParams.forEach(param => {
          if (param.key && param.value) {
            config.configuration[param.key] = param.value;
          }
        });
        break;

      case DataWarehouseType.SNOWFLAKE:
        config.configuration = {
          accountName: values.snowflakeConfig?.accountName,
          warehouse: values.snowflakeConfig?.warehouse,
          database: values.snowflakeConfig?.database,
          schema: values.snowflakeConfig?.schema,
          role: values.snowflakeConfig?.role,
        };
        // Add additional params if any
        values.snowflakeConfig?.additionalParams.forEach(param => {
          if (param.key && param.value) {
            config.configuration[param.key] = param.value;
          }
        });
        break;

      case DataWarehouseType.POSTGRES:
      case DataWarehouseType.SQLSERVER:
        config.configuration = {
          host: values.redshiftConfig?.host,
          port: parseInt(values.redshiftConfig?.port || "5439"),
          database: values.redshiftConfig?.database,
          username: values.redshiftConfig?.username,
          password: values.redshiftConfig?.password,
        };
        // Add additional params if any
        values.redshiftConfig?.additionalParams.forEach(param => {
          if (param.key && param.value) {
            config.configuration[param.key] = param.value;
          }
        });
        break;
    }

    return config;
  };

  const handleSubmit = async (values: ConnectionFormValues) => {
    const requestData = CreateUpdateConnectionRequest.create({
      name: values.connectionName,
      description: values.description,
      data_warehouse_type: WAREHOUSE_TYPE_MAPPING[values.dataWarehouseType as keyof typeof WAREHOUSE_TYPE_MAPPING],
      data_warehouse_config: JSON.stringify(formatConnectionConfig(values)),
      validate: false,
    });

    const response = await createConnection(requestData);
    if (response) {
      setConnectionId(response?.id || "");
      showToast("Connection created", "Your connection has been created successfully", "success");
    }
  };

  const renderStepOne = (formik: any) => (
    <VStack spacing={6} align="stretch" w="100%">
      <FormControl isRequired>
        <FormLabel>Connection Name</FormLabel>
        <Field
          as={Input}
          name="connectionName"
          placeholder="Enter a name for your connection"
        />
      </FormControl>

      <FormControl>
        <FormLabel>Description</FormLabel>
        <Field
          as={Textarea}
          name="description"
          placeholder="Enter a description for your connection"
        />
      </FormControl>

      <FormControl isRequired>
        <FormLabel>Select Data Warehouse</FormLabel>
        <Stack spacing={4}>
          {WAREHOUSE_TYPES.map((warehouse) => (
            <Card
              key={warehouse.id}
              cursor="pointer"
              onClick={() => {
                formik.setFieldValue("dataWarehouseType", warehouse.id);
                setSelectedType(warehouse.id);
              }}
              bg={formik.values.dataWarehouseType === warehouse.id ? "blue.50" : "white"}
              borderColor={formik.values.dataWarehouseType === warehouse.id ? "blue.500" : "gray.200"}
              borderWidth="1px"
            >
              <CardBody>
                <HStack>
                  <Text fontSize="xl">{warehouse.icon}</Text>
                  <Text>{warehouse.name}</Text>
                </HStack>
              </CardBody>
            </Card>
          ))}
        </Stack>
      </FormControl>

      <Button
        colorScheme="blue"
        isDisabled={!formik.values.connectionName || !formik.values.dataWarehouseType}
        onClick={() => setCurrentStep(2)}
        mt={4}
      >
        Next
      </Button>
    </VStack>
  );

  const renderAdditionalParams = (formik: any, configPath: string) => (
    <Box mt={4}>
      <FormLabel>Additional Configuration</FormLabel>
      <VStack align="stretch" spacing={4}>
        {formik.values[configPath].additionalParams.map((_: any, index: number) => (
          <HStack key={index}>
            <FormControl>
              <Field
                as={Input}
                name={`${configPath}.additionalParams.${index}.key`}
                placeholder="Parameter name"
              />
            </FormControl>
            <FormControl>
              <Field
                as={Input}
                name={`${configPath}.additionalParams.${index}.value`}
                placeholder="Value"
              />
            </FormControl>
            <IconButton
              aria-label="Remove parameter"
              icon={<DeleteIcon />}
              onClick={() => {
                const newParams = [...formik.values[configPath].additionalParams];
                newParams.splice(index, 1);
                formik.setFieldValue(`${configPath}.additionalParams`, newParams);
              }}
            />
          </HStack>
        ))}
        <Button
          leftIcon={<AddIcon />}
          onClick={() => {
            formik.setFieldValue(`${configPath}.additionalParams`, [
              ...formik.values[configPath].additionalParams,
              { key: "", value: "" },
            ]);
          }}
          size="sm"
        >
          Add Parameter
        </Button>
      </VStack>
    </Box>
  );

  const renderBigQueryConfig = (formik: any) => (
    <VStack spacing={6} align="stretch">
      <FormControl isRequired>
        <FormLabel>Project ID</FormLabel>
        <Field
          as={Input}
          name="bigQueryConfig.projectId"
          placeholder="Enter your Google Cloud project ID"
        />
        <FormHelperText>The ID of your Google Cloud project</FormHelperText>
      </FormControl>

      <FormControl isRequired>
        <FormLabel>Service Account Key</FormLabel>
        <Field
          as={Textarea}
          name="bigQueryConfig.serviceAccountKey"
          placeholder="Paste your service account key JSON"
          h="200px"
        />
        <FormHelperText>The service account key JSON from Google Cloud Console</FormHelperText>
      </FormControl>

      {renderAdditionalParams(formik, "bigQueryConfig")}
    </VStack>
  );

  const renderSnowflakeConfig = (formik: any) => (
    <VStack spacing={6} align="stretch">
      <FormControl isRequired>
        <FormLabel>Account Name</FormLabel>
        <Field
          as={Input}
          name="snowflakeConfig.accountName"
          placeholder="Enter your Snowflake account name"
        />
      </FormControl>

      <FormControl isRequired>
        <FormLabel>Warehouse</FormLabel>
        <Field
          as={Input}
          name="snowflakeConfig.warehouse"
          placeholder="Enter warehouse name"
        />
      </FormControl>

      <FormControl isRequired>
        <FormLabel>Database</FormLabel>
        <Field
          as={Input}
          name="snowflakeConfig.database"
          placeholder="Enter database name"
        />
      </FormControl>

      <FormControl isRequired>
        <FormLabel>Schema</FormLabel>
        <Field
          as={Input}
          name="snowflakeConfig.schema"
          placeholder="Enter schema name"
        />
      </FormControl>

      <FormControl>
        <FormLabel>Role</FormLabel>
        <Field
          as={Input}
          name="snowflakeConfig.role"
          placeholder="Enter role name (optional)"
        />
      </FormControl>

      {renderAdditionalParams(formik, "snowflakeConfig")}
    </VStack>
  );

  const renderRedshiftConfig = (formik: any) => (
    <VStack spacing={6} align="stretch">
      <FormControl isRequired>
        <FormLabel>Host</FormLabel>
        <Field
          as={Input}
          name="redshiftConfig.host"
          placeholder="Enter your Redshift host"
        />
      </FormControl>

      <FormControl isRequired>
        <FormLabel>Port</FormLabel>
        <Field
          as={Input}
          name="redshiftConfig.port"
          placeholder="Enter port number"
        />
      </FormControl>

      <FormControl isRequired>
        <FormLabel>Database</FormLabel>
        <Field
          as={Input}
          name="redshiftConfig.database"
          placeholder="Enter database name"
        />
      </FormControl>

      <FormControl isRequired>
        <FormLabel>Username</FormLabel>
        <Field
          as={Input}
          name="redshiftConfig.username"
          placeholder="Enter username"
        />
      </FormControl>

      <FormControl isRequired>
        <FormLabel>Password</FormLabel>
        <Field
          as={Input}
          type="password"
          name="redshiftConfig.password"
          placeholder="Enter password"
        />
      </FormControl>

      {renderAdditionalParams(formik, "redshiftConfig")}
    </VStack>
  );

  const renderStepTwo = (formik: any) => (
    <VStack spacing={6} align="stretch" w="100%">
      <HStack>
        <IconButton
          aria-label="Back"
          icon={<ChevronLeftIcon />}
          onClick={() => setCurrentStep(1)}
        />
        <Heading size="md">Configure Connection</Heading>
      </HStack>

      {formik.values.dataWarehouseType === DataWarehouseType.GOOGLE_BIGQUERY && renderBigQueryConfig(formik)}
      {formik.values.dataWarehouseType === DataWarehouseType.SNOWFLAKE && renderSnowflakeConfig(formik)}
      {(formik.values.dataWarehouseType === DataWarehouseType.POSTGRES || formik.values.dataWarehouseType === DataWarehouseType.SQLSERVER) && renderRedshiftConfig(formik)}

      <Button
        colorScheme="blue"
        type="submit"
        mt={4}
      >
        Create Connection
      </Button>
    </VStack>
  );

  return (
    <Container maxW="container.lg" py={8}>
      {!connectionId ? (
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {(formik) => (
            <Form>  
              {currentStep === 1 && renderStepOne(formik)}
              {currentStep === 2 && renderStepTwo(formik)}
            </Form>
          )}
        </Formik>
      ) : (
        <VStack spacing={8} align="stretch">
          <HStack justify="space-between">
            <Heading size="lg">Connection Details</Heading>
            <Button
              variant="ghost"
              onClick={() => {
                setConnectionId("");
                setCurrentStep(1);
              }}
            >
              Create Another Connection
            </Button>
          </HStack>
          <MetadataExplorer
            connectionId={connectionId}
            dataWarehouseType={selectedType}
          />
        </VStack>
      )}
    </Container>
  );
};

export default IntegrationPage; 